import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { globalMutate } from "@utils/swr";
import { createVisit } from "@services/visits/create";
import { createAttachmentsVisit } from "@services/visits/createAttachments";
import { config } from "@/config";
import { useParams } from "react-router-dom";
import { VisitTypeHelper } from "@typings/entities/visit/enums";
import { SAMPLE_TYPE_OPTIONS } from "@typings/entities/samples/enums";
import RadioInput from "@commons/Input/Radio";
import MultiSelectInput from "@commons/Input/MultiSelect";
import UploadInput from "@commons/Input/Upload";
import Text from "@commons/Typography/Text";
import type { OkResponse } from "@typings/api";
import {
  visitDefaultValues,
  visitSchema,
  type VisitFormData,
} from "@modules/community/schemas/visitSchema";

function ModalNewVisit() {
  const { isModalOpen, closeModal, setIsLoading } = usePage();
  const open = isModalOpen("newVisit");
  const { id } = useParams<{ id: string }>();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VisitFormData>({
    resolver: zodResolver(visitSchema),
    defaultValues: visitDefaultValues,
  });

  const onSubmit = async (
    data: VisitFormData,
    locationId: string | undefined,
  ) => {
    if (!locationId) {
      toast.error("Localização não encontrada");
      return;
    }

    try {
      setIsLoading(true);

      const visitCreate = await createVisit({
        locationId,
        visitType: data.visitType ?? undefined,
        date: data.date,
        responsibleTeam: data.responsibleTeam,
        sampleTypes: data.sampleTypes,
      });

      const promises: Promise<OkResponse>[] = [];

      if (data.formAttachments?.length) {
        promises.push(
          createAttachmentsVisit({
            id: visitCreate.id,
            files: data.formAttachments,
            fileType: "form",
          }),
        );
      }

      if (data.photoReportAttachments?.length) {
        promises.push(
          createAttachmentsVisit({
            id: visitCreate.id,
            files: data.photoReportAttachments,
            fileType: "photo_report",
          }),
        );
      }

      if (promises.length) await Promise.all(promises);

      toast.success("Visita criada com sucesso");

      await globalMutate(["fetch-get-visits"]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar visita";

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
    reset();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Registrar Visita"
      onClose={handleClose}
      onConfirm={handleSubmit((data) => onSubmit(data, id))}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-5xl relative"
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Controller
          name="date"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Data da Visita"
              textSize="sm"
              type="date"
              fullWidth
              error={!!errors.date}
              helperText={errors.date?.message}
            />
          )}
        />
        <div />

        <div>
          <Controller
            name="responsibleTeam"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Equipe Responsável"
                textSize="sm"
                fullWidth
                error={!!errors.responsibleTeam}
                helperText={errors.responsibleTeam?.message}
              />
            )}
          />
        </div>

        <div className="flex items-end">
          <Controller
            name="visitType"
            control={control}
            render={({ field }) => (
              <RadioInput
                {...field}
                value={field.value || ""}
                onChange={(e) => {
                  field.onChange(
                    e.target.value === "" ? undefined : e.target.value,
                  );
                }}
                error={!!errors.visitType}
                helperText={errors.visitType?.message}
                options={VisitTypeHelper.getOptions()}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="sampleTypes"
            control={control}
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                label="Amostras Coletadas"
                value={field.value ?? []}
                options={SAMPLE_TYPE_OPTIONS}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Text>Formulário Preenchido</Text>
          <Text customVariant="gray" size="sm" className="mb-2">
            Caso tenha preenchido o formulário, por favor anexar.
          </Text>

          <Controller
            name="formAttachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.formAttachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.formAttachments.message}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <Text className="mb-2">Relatório Fotográfico</Text>

          <Controller
            name="photoReportAttachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.photoReportAttachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.photoReportAttachments.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewVisit;
